import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { AlertCircle, RefreshCw, CheckCircle2, FileX, ExternalLink, Upload, Clock } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useAuth } from '@/context/auth-context';

// ---------------------------------------------------------------------------
// Invoice review (super-admin).
//
// The invoice agent refuses to book an invoice whose amount it cannot read —
// writing 0.00 would put a false number in the accounts. Those documents land
// here: the PDF on the left, a decision on the right. One question per screen,
// answered from the document itself.
//
// Deliberately NO pre-filled guess. A plausible wrong number with a green
// "confirm" button next to it manufactures assent; a blank field makes the
// person read the PDF. That is the whole value of this screen.
// ---------------------------------------------------------------------------

const API = '/api/invoice-review';

interface QueueItem {
  provider: string;
  date: string;
  notes: string;
  pdfPath: string;
}

interface QueueResponse {
  pending: QueueItem[];
  resolvedCount: number;
}

interface HistoryRow {
  pdfPath: string;
  fileName: string;
  /** Invoice date parsed from the filename; null when it could not be read. */
  date: string | null;
  provider: string;
  sizeBytes: number;
  sha256: string;
  outcome: 'amount' | 'not_an_invoice' | 'unresolved' | 'human';
  amount: number | null;
  currency: string | null;
  taxAmount: number | null;
  taxRate: number | null;
  reason: string | null;
  gmailMessageId: string | null;
  gmailAccount: string | null;
  subject: string | null;
  source: string | null;
  resolvedBy?: string | null;
  resolvedAt?: string | null;
  /**
   * Set on the receipt of an invoice the agent also holds: that invoice's path.
   * The receipt is the same payment, booked once through the invoice — never
   * on its own (ops-server/invoice-agent/src/utils/email-pairs.ts).
   */
  pairedInvoice?: string | null;
}

interface BankTx {
  id: string;
  date: string;
  merchant: string;
  amount: number;
  currency: string;
  origAmount?: number;
  origCurrency?: string;
  receiptPaths: string[];
}

interface BankResponse {
  notGenerated?: boolean;
  hint?: string;
  generatedAt: string | null;
  summary: Record<string, number> | null;
  matched: Array<{ transaction: BankTx; invoice: HistoryRow; reason: { amount: string; dayDelta: number } }>;
  ambiguous: Array<{ transaction: BankTx; candidates: HistoryRow[]; why: string }>;
  unmatchedTransactions: BankTx[];
  unmatchedInvoices: HistoryRow[];
  humanLinks?: Array<{ transactionId: string; decision: string }>;
}

interface HistoryResponse {
  rows: HistoryRow[];
  generatedAt: string | null;
  manifestCoverage: number;
  notGenerated?: boolean;
  hint?: string;
}

const CURRENCIES = ['EUR', 'USD', 'GBP'] as const;
type Currency = (typeof CURRENCIES)[number];

/** The four views, each of which may fail independently of the others. */
type ViewKey = 'queue' | 'history' | 'bank' | 'analytics';

/**
 * Shown instead of a view whose data could not be loaded.
 *
 * The alternative — rendering the view with no rows — states something false:
 * "no unmatched payments", "no invoices extracted". On a reconciliation screen
 * that is the difference between a clean month and a broken endpoint.
 */
function ViewUnavailable({
  view,
  detail,
  onRetry,
}: {
  view: string;
  detail: string;
  onRetry: () => void;
}) {
  return (
    <Card>
      <CardContent className="p-8 space-y-3 text-center">
        <AlertCircle className="h-6 w-6 mx-auto text-destructive" />
        <div className="font-medium">{view} could not be loaded</div>
        <p className="text-sm text-muted-foreground">
          This is <strong>not</strong> an empty result — the figures below would have been wrong,
          so nothing is shown.
        </p>
        <pre className="text-xs text-left bg-muted p-2 rounded overflow-x-auto">{detail}</pre>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Where the figures come from (GET /status): what the invoice agent last pushed
 * to Postgres from P3 (`agent.sh state-sync push`), and how its last weekly run
 * ended. `ready` is false when the tables do not exist; `hint` says what to run.
 */
interface AgentStatus {
  ready: boolean;
  hint: string | null;
  agent: {
    pushedAt: string;
    commit: string | null;
    lastWeekly: { exitCode: number; finishedAt: string } | null;
    documents?: Record<string, { present: boolean; generatedAt: string | null }>;
  } | null;
}

const when = (iso: string) => new Date(iso).toLocaleString();

/**
 * A failed request's body, as a sentence. The backend answers data routes with
 * `{ error, hint }` when it cannot see the agent's directory; showing the raw
 * JSON would bury the one line that says how to fix it.
 *
 * Only a body that is NOT ours is shortened (a proxy's HTML error page: its
 * first lines are enough to recognise it). Our `{ error, hint }` is never cut —
 * the bridge hint alone is over 300 characters, and the cut fell inside the
 * command to run.
 */
function describeFailure(text: string): string {
  try {
    const parsed = JSON.parse(text) as { error?: string; hint?: string };
    if (parsed && typeof parsed.error === 'string') {
      return parsed.hint ? `${parsed.error} — ${parsed.hint}` : parsed.error;
    }
  } catch {
    /* not JSON: shown verbatim, but bounded */
  }
  return text.slice(0, 300);
}

export function InvoiceReview() {
  const { user } = useAuth();
  const [tab, setTab] = useState<'queue' | 'history' | 'bank' | 'analytics'>('queue');
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [bank, setBank] = useState<BankResponse | null>(null);
  /** Why a view is unavailable, so it is never mistaken for empty. */
  const [viewErrors, setViewErrors] = useState<Record<ViewKey, string | null>>({
    queue: null,
    history: null,
    bank: null,
    analytics: null,
  });
  const [bankView, setBankView] = useState<'undecided' | 'matched' | 'orphan-invoices'>('undecided');
  const [data, setData] = useState<QueueResponse | null>(null);
  const [history, setHistory] = useState<HistoryResponse | null>(null);
  const [historyFilter, setHistoryFilter] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agentStatus, setAgentStatus] = useState<AgentStatus | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  // Form state for the current item
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<Currency>('EUR');
  const [taxAmount, setTaxAmount] = useState('');
  const [taxRate, setTaxRate] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [queueRes, historyRes, bankRes, analyticsRes] = await Promise.all([
        authFetch(`${API}/queue`),
        authFetch(`${API}/history`),
        authFetch(`${API}/bank`),
        authFetch(`${API}/analytics`),
      ]);
      // Partial availability is deliberate — a broken History tab must not hide
      // the queue, which is the part that blocks money entering the books. What
      // is NOT acceptable is letting a failed view render as valid data: an
      // empty Bank tab reads as "nothing left to reconcile", and after a
      // refresh the previous numbers would simply stay on screen as if current.
      // So a failure CLEARS the view and records why, and the tab says so.
      //
      // The queue follows the same rule. It used to throw instead, which put
      // "Nothing to review. Every downloaded invoice was priced automatically."
      // under the error and left the three other views unread, at (0).
      const failures: Record<ViewKey, string | null> = {
        queue: null,
        history: null,
        bank: null,
        analytics: null,
      };
      const take = async <T,>(
        key: ViewKey,
        res: Response,
        set: (value: T | null) => void
      ): Promise<void> => {
        if (res.ok) {
          set((await res.json()) as T);
          return;
        }
        set(null);
        failures[key] = `HTTP ${res.status} — ${describeFailure(await res.text())}`;
      };
      await take<QueueResponse>('queue', queueRes, setData);
      await take<HistoryResponse>('history', historyRes, setHistory);
      await take<BankResponse>('bank', bankRes, setBank);
      await take<Analytics>('analytics', analyticsRes, setAnalytics);
      setViewErrors(failures);

      // Where the figures come from. Informational only: a failure here must
      // not take the page down — the data routes above already answer 503,
      // with the fix, when the directory is gone.
      try {
        const statusRes = await authFetch(`${API}/status`);
        setAgentStatus(statusRes.ok ? ((await statusRes.json()) as AgentStatus) : null);
      } catch {
        setAgentStatus(null);
      }

      setActiveIndex(0);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const pending = data?.pending ?? [];
  const active = pending[activeIndex] ?? null;

  // Clear the form whenever the document changes, so a value typed for one
  // invoice can never be submitted against the next one.
  useEffect(() => {
    setAmount('');
    setTaxAmount('');
    setTaxRate('');
    setCurrency('EUR');
  }, [active?.pdfPath]);

  const submit = useCallback(
    async (body: Record<string, unknown>) => {
      if (!active) return;
      setSaving(true);
      setError(null);
      try {
        const res = await authFetch(`${API}/resolve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pdfPath: active.pdfPath, ...body }),
        });
        if (!res.ok) {
          const detail = await res.json().catch(() => ({}));
          throw new Error(detail.error || `Save failed: ${res.status}`);
        }
        setFlash(`Saved — ${active.notes}`);
        await load();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [active, load]
  );

  const amountNum = Number(amount);
  const taxNum = taxAmount === '' ? null : Number(taxAmount);
  const rateNum = taxRate === '' ? null : Number(taxRate);
  const taxHalfFilled = (taxNum === null) !== (rateNum === null);
  const canSave =
    !!active &&
    !saving &&
    Number.isFinite(amountNum) &&
    amountNum > 0 &&
    !taxHalfFilled &&
    (taxNum === null || (Number.isFinite(taxNum) && Number.isFinite(rateNum as number)));

  // Defence in depth. The API is the real boundary — every endpoint here is
  // behind a live super-admin check, so a non-admin gets 403 regardless. This
  // just avoids rendering a finance screen (and its vendor names) to someone
  // who typed the URL, instead of showing them a wall of failed requests.
  if (user && user.role !== 'super_admin') {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Invoice review is restricted to super-admins.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Invoice review</h1>
          <p className="text-sm text-muted-foreground">
            Invoices the agent refused to book because it could not read the amount.
            Nothing here is in the accounts yet.
          </p>
          {agentStatus && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="agent-status">
              {agentStatus.ready && agentStatus.agent ? (
                <>
                  Pushed by the invoice agent {when(agentStatus.agent.pushedAt)}
                  {agentStatus.agent.commit ? ` (commit ${agentStatus.agent.commit.slice(0, 9)})` : ''}
                  {agentStatus.agent.lastWeekly ? (
                    agentStatus.agent.lastWeekly.exitCode === 0 ? (
                      ` · last weekly run ${when(agentStatus.agent.lastWeekly.finishedAt)}: OK`
                    ) : (
                      <>
                        {` · last weekly run ${when(agentStatus.agent.lastWeekly.finishedAt)}: `}
                        <span className="text-destructive font-medium">
                          FAILED (exit {agentStatus.agent.lastWeekly.exitCode})
                        </span>
                      </>
                    )
                  ) : (
                    ' · no weekly run recorded yet'
                  )}
                  {agentStatus.agent.documents?.history?.generatedAt
                    ? ` · extraction history from ${when(agentStatus.agent.documents.history.generatedAt)}`
                    : ' · no extraction history yet'}
                </>
              ) : (
                <>Nothing from the invoice agent yet. {agentStatus.hint}</>
              )}
            </p>
          )}
        </div>
        <Button variant="outline" onClick={() => void load()} disabled={loading}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <div className="flex rounded-md border overflow-hidden">
          <button
            className={`px-3 py-1.5 text-sm ${tab === 'queue' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
            onClick={() => setTab('queue')}
          >
            To review ({viewErrors.queue ? '⚠' : pending.length})
          </button>
          <button
            className={`px-3 py-1.5 text-sm border-l ${tab === 'history' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
            onClick={() => setTab('history')}
          >
            History ({viewErrors.history ? '⚠' : (history?.rows.length ?? 0)})
          </button>
          <button
            className={`px-3 py-1.5 text-sm border-l ${tab === 'bank' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
            onClick={() => setTab('bank')}
          >
            Bank (
              {viewErrors.bank
                ? '⚠'
                : (bank?.ambiguous.length ?? 0) + (bank?.unmatchedTransactions.length ?? 0)}
            )
          </button>
          <button
            className={`px-3 py-1.5 text-sm border-l ${tab === 'analytics' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
            onClick={() => setTab('analytics')}
          >
            Analytics{viewErrors.analytics ? ' ⚠' : ''}
          </button>
        </div>
        {data && <Badge variant="secondary">{data.resolvedCount} resolved</Badge>}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {flash && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{flash}</AlertDescription>
        </Alert>
      )}

      {tab === 'analytics' ? (
        viewErrors.analytics ? (
          <ViewUnavailable view="Analytics" detail={viewErrors.analytics} onRetry={load} />
        ) : (
          <AnalyticsTab data={analytics} />
        )
      ) : tab === 'bank' ? (
        viewErrors.bank ? (
          <ViewUnavailable view="Bank reconciliation" detail={viewErrors.bank} onRetry={load} />
        ) : (
          <BankTab bank={bank} view={bankView} setView={setBankView} onDecided={load} />
        )
      ) : tab === 'history' ? (
        viewErrors.history ? (
          <ViewUnavailable view="Extraction history" detail={viewErrors.history} onRetry={load} />
        ) : (
          <HistoryTab
            history={history}
            filter={historyFilter}
            setFilter={setHistoryFilter}
            expanded={expanded}
            setExpanded={setExpanded}
          />
        )
      ) : viewErrors.queue ? (
        <ViewUnavailable view="Review queue" detail={viewErrors.queue} onRetry={load} />
      ) : pending.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-muted-foreground">
            <CheckCircle2 className="h-8 w-8 mx-auto mb-3 text-green-600" />
            Nothing to review. Every downloaded invoice was priced automatically.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4">
          {/* ── The document ── */}
          <Card className="overflow-hidden">
            <CardContent className="p-0 h-[70vh]">
              <DocViewer path={active?.pdfPath ?? null} />
            </CardContent>
          </Card>

          {/* ── The decision ── */}
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4 space-y-1">
                <div className="text-xs uppercase text-muted-foreground">
                  {activeIndex + 1} of {pending.length}
                </div>
                <div className="font-medium">{active?.notes}</div>
                <div className="text-sm text-muted-foreground">{active?.date}</div>
                <div className="text-xs text-muted-foreground break-all">{active?.pdfPath}</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="text-sm font-medium">Read the total off the PDF</div>

                <div className="grid grid-cols-[1fr_90px] gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground">Net amount</label>
                    <Input
                      autoFocus
                      inputMode="decimal"
                      placeholder="135.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Currency</label>
                    <select
                      className="w-full h-10 rounded-md border bg-background px-2 text-sm"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value as Currency)}
                    >
                      {CURRENCIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground">Tax amount (optional)</label>
                    <Input
                      inputMode="decimal"
                      placeholder="28.35"
                      value={taxAmount}
                      onChange={(e) => setTaxAmount(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Tax rate %</label>
                    <Input
                      inputMode="decimal"
                      placeholder="21"
                      value={taxRate}
                      onChange={(e) => setTaxRate(e.target.value)}
                    />
                  </div>
                </div>

                {taxHalfFilled && (
                  <p className="text-xs text-destructive">
                    Give both tax amount and rate, or neither.
                  </p>
                )}

                <Button
                  className="w-full"
                  disabled={!canSave}
                  onClick={() =>
                    void submit({
                      decision: 'amount',
                      amount: amountNum,
                      currency,
                      ...(taxNum !== null ? { taxAmount: taxNum, taxRate: rateNum } : {}),
                    })
                  }
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Save amount
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="text-sm font-medium">…or this is not a bill</div>
                <p className="text-xs text-muted-foreground">
                  Payment reminders, statements and terms restate a debt that is already
                  booked. Marking one here keeps it out of the accounts for good.
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={saving}
                  onClick={() =>
                    void submit({ decision: 'not_an_invoice', reason: 'not a bill (human review)' })
                  }
                >
                  <FileX className="h-4 w-4 mr-2" />
                  Not an invoice
                </Button>
              </CardContent>
            </Card>

            {pending.length > 1 && (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  className="flex-1"
                  disabled={activeIndex === 0}
                  onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="ghost"
                  className="flex-1"
                  disabled={activeIndex >= pending.length - 1}
                  onClick={() => setActiveIndex((i) => Math.min(pending.length - 1, i + 1))}
                >
                  Skip
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// History: every downloaded PDF, what the rules made of it, and where it came
// from. Includes the documents that were deliberately NOT booked — those are
// the interesting ones, and reading Invoice Ninja instead would hide them.
// ---------------------------------------------------------------------------

const OUTCOME_LABEL: Record<HistoryRow['outcome'], { text: string; variant: 'default' | 'secondary' | 'destructive' }> = {
  amount: { text: 'Booked', variant: 'default' },
  human: { text: 'Human', variant: 'default' },
  not_an_invoice: { text: 'Not a bill', variant: 'secondary' },
  unresolved: { text: 'Needs review', variant: 'destructive' },
};

/**
 * What the agent did with this document. A paired receipt is checked first:
 * whatever its own extraction says, sync:ninja skips it and books the payment
 * through the invoice. Labelled "Booked", it sat next to its invoice with the
 * same amount and read as the double booking the pairing exists to prevent.
 */
function outcomeLabel(row: HistoryRow): { text: string; variant: 'default' | 'secondary' | 'destructive' } {
  if (row.pairedInvoice) return { text: 'Receipt', variant: 'secondary' };
  return OUTCOME_LABEL[row.outcome] ?? OUTCOME_LABEL.unresolved;
}

function money(row: HistoryRow): string {
  if (row.amount == null) return '—';
  const gross = row.amount + (row.taxAmount ?? 0);
  const base = `${row.currency ?? ''} ${gross.toFixed(2)}`;
  return row.taxAmount ? `${base} (net ${row.amount.toFixed(2)} + tax ${row.taxAmount.toFixed(2)})` : base;
}

function HistoryTab({
  history,
  filter,
  setFilter,
  expanded,
  setExpanded,
}: {
  history: HistoryResponse | null;
  filter: string;
  setFilter: (v: string) => void;
  expanded: string | null;
  setExpanded: (v: string | null) => void;
}) {
  const rows = useMemo(() => {
    const all = history?.rows ?? [];
    const q = filter.trim().toLowerCase();
    const matched = q
      ? all.filter((r) =>
          [r.fileName, r.provider, r.subject, r.currency, r.outcome, r.date]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q))
        )
      : all;

    // Newest first. Sorted here as well as in the agent so the order survives
    // filtering and does not depend on how the file happened to be written.
    // ISO dates compare correctly as strings; undated rows sink to the bottom
    // rather than masquerading as the most recent.
    return [...matched].sort((a, b) => {
      if (a.date === b.date) return a.fileName.localeCompare(b.fileName);
      if (!a.date) return 1;
      if (!b.date) return -1;
      return b.date.localeCompare(a.date);
    });
  }, [history, filter]);

  if (history?.notGenerated) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          The invoice agent has not pushed an extraction history yet.
          {history.hint ? <> {history.hint}</> : null}
        </AlertDescription>
      </Alert>
    );
  }

  const noProvenance = (history?.manifestCoverage ?? 0) === 0 && rows.length > 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          placeholder="Filter by provider, file, subject…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-sm"
        />
        {history?.generatedAt && (
          <span className="text-xs text-muted-foreground">
            generated {new Date(history.generatedAt).toLocaleString()}
          </span>
        )}
        <Badge variant="secondary">
          {Math.round((history?.manifestCoverage ?? 0) * 100)}% with a source email
        </Badge>
      </div>

      {noProvenance && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No source emails recorded yet. These PDFs were downloaded before provenance
            tracking existed — the message id lives only in the download step, so it is
            backfilled on the next <code className="text-xs">download:all</code> run.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr className="text-left">
                <th className="p-2 font-medium whitespace-nowrap">Date ↓</th>
                <th className="p-2 font-medium">Provider</th>
                <th className="p-2 font-medium">Document</th>
                <th className="p-2 font-medium">Outcome</th>
                <th className="p-2 font-medium text-right">Amount</th>
                <th className="p-2 font-medium">Source</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isOpen = expanded === row.pdfPath;
                const label = outcomeLabel(row);
                return (
                  <React.Fragment key={row.pdfPath}>
                    <tr className="border-b hover:bg-muted/30">
                      <td className="p-2 whitespace-nowrap tabular-nums">
                        {row.date ?? <span className="text-muted-foreground">no date</span>}
                      </td>
                      <td className="p-2 whitespace-nowrap">{row.provider}</td>
                      <td className="p-2 max-w-[320px] truncate" title={row.fileName}>
                        {row.fileName}
                      </td>
                      <td className="p-2">
                        <Badge
                          variant={label.variant}
                          title={
                            row.pairedInvoice
                              ? `Receipt of ${row.pairedInvoice.split('/').pop()}: the payment is booked once, through that invoice`
                              : undefined
                          }
                        >
                          {label.text}
                        </Badge>
                      </td>
                      <td
                        className={`p-2 text-right whitespace-nowrap${row.pairedInvoice ? ' text-muted-foreground' : ''}`}
                      >
                        {money(row)}
                      </td>
                      <td className="p-2 text-xs text-muted-foreground whitespace-nowrap">
                        {row.gmailAccount ?? '—'}
                      </td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpanded(isOpen ? null : row.pdfPath)}
                        >
                          {isOpen ? 'Hide' : 'Details'}
                        </Button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b bg-muted/20">
                        <td colSpan={7} className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                            <div className="space-y-1">
                              <div className="font-medium text-sm mb-1">Extracted fields</div>
                              <Field k="Outcome" v={label.text} />
                              {row.pairedInvoice && <Field k="Booked through" v={row.pairedInvoice} />}
                              <Field k="Net amount" v={row.amount != null ? row.amount.toFixed(2) : '—'} />
                              <Field k="Currency" v={row.currency ?? '—'} />
                              <Field k="Tax" v={row.taxAmount != null ? `${row.taxAmount.toFixed(2)} (${row.taxRate}%)` : '—'} />
                              {row.reason && <Field k="Reason" v={row.reason} />}
                              {row.resolvedBy && <Field k="Decided by" v={`${row.resolvedBy} · ${row.resolvedAt}`} />}
                            </div>
                            <div className="space-y-1">
                              <div className="font-medium text-sm mb-1">Source</div>
                              <Field k="Mailbox" v={row.gmailAccount ?? 'not recorded'} />
                              <Field k="Subject" v={row.subject ?? 'not recorded'} />
                              <Field k="Method" v={row.source ?? 'not recorded'} />
                              <Field k="File" v={row.pdfPath} />
                              <Field k="SHA-256" v={row.sha256} />
                              <Field k="Size" v={`${(row.sizeBytes / 1024).toFixed(0)} KB`} />
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <OpenDocument path={row.pdfPath}>
                              Open PDF <ExternalLink className="inline h-3 w-3" />
                            </OpenDocument>
                            {row.gmailMessageId && (
                              <a
                                className="text-xs underline"
                                href={`https://mail.google.com/mail/u/0/#all/${row.gmailMessageId}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Open source email <ExternalLink className="inline h-3 w-3" />
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                    No document matches this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bank reconciliation.
//
// Only the CERTAIN pairs are booked automatically. Everything else lands here
// for a person to decide, with the system's candidates offered as suggestions
// rather than pre-applied — a suggestion the human accepts is a decision; a
// suggestion the system applies itself is a guess wearing a decision's clothes.
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Bank reconciliation — built for someone clearing a queue, not browsing data.
//
// Split view: the decision on the left, the document on the right. Selecting a
// candidate previews it instantly, so comparing a payment against two similar
// invoices is a glance rather than two browser tabs. Keyboard: j/k to move,
// 1-9 to link the n-th candidate, n = no invoice, i = ignore.
// ---------------------------------------------------------------------------

type DecideFn = (transactionId: string, decision: string, pdfPath?: string) => void;

interface WorkItem {
  tx: BankTx;
  candidates: HistoryRow[];
  why: string | null;
}

function eur(n: number, cur = 'EUR'): string {
  return `${cur} ${n.toFixed(2)}`;
}

// ── Documents ──────────────────────────────────────────────────────────
//
// A document is fetched through authFetch and shown from a blob: URL — never
// loaded by pointing an <iframe>, <img> or <a> at the API. Those elements send
// cookies, not the Authorization header, and in production no auth cookie
// exists on the admin origin: sign-in posts to api.pagemind.ai, so the browser
// never stores one here. Every document answered 401 {"reason":"no_token"},
// found live on admin.pagemind.ai on 2026-10-08. authFetch sends the token the
// rest of the page already uses, so a document loads whenever the lists do.
//
// The blob's type is OURS, taken from the extension the server already
// validated, never from the response: a blob typed application/pdf goes to the
// browser's PDF viewer and cannot be parsed as HTML, whatever bytes it holds.
// These files are attachments from outside senders; that is the guarantee that
// matters, and it no longer depends on every proxy in between keeping the
// backend's nosniff and CSP headers.

const DOC_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

function docType(p: string): string | null {
  const ext = p.split('.').pop()?.toLowerCase() ?? '';
  return DOC_TYPES[ext] ?? null;
}

function docEndpoint(p: string): string {
  // Bank receipts live under their own root and may be images; invoices are
  // always PDFs under invoices/. Two endpoints, chosen by where the file lives.
  return p.startsWith('bank-receipts/') || p.startsWith('uploaded-invoices/')
    ? `${API}/bank/receipt?path=${encodeURIComponent(p)}`
    : `${API}/pdf?path=${encodeURIComponent(p)}`;
}

/** Fetch one document and return an object URL the caller must revoke. */
async function fetchDocument(p: string): Promise<string> {
  const type = docType(p);
  if (!type) throw new Error(`Not a PDF or image: ${p}`);
  const res = await authFetch(docEndpoint(p));
  if (!res.ok) {
    throw new Error(`Could not load the document (HTTP ${res.status}): ${(await res.text()).slice(0, 300)}`);
  }
  return URL.createObjectURL(new Blob([await res.arrayBuffer()], { type }));
}

// PDF open parameters, read by the browser's viewer rather than sent to the
// server: `pagemode=none` hides the page-thumbnail strip, which eats a third of
// the width for no benefit on a one or two page invoice, and `view=FitH` fits
// the page across so the totals are legible without zooming.
function withViewerParams(url: string, p: string): string {
  return docType(p) === 'application/pdf' ? `${url}#pagemode=none&view=FitH` : url;
}

type DocState = { url: string | null; error: string | null };

function useDocumentUrl(p: string | null): DocState {
  const [state, setState] = useState<DocState>({ url: null, error: null });
  useEffect(() => {
    setState({ url: null, error: null });
    if (!p) return;
    let cancelled = false;
    let created: string | null = null;
    fetchDocument(p).then(
      (url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setState({ url, error: null });
      },
      (e: Error) => {
        if (!cancelled) setState({ url: null, error: e.message });
      }
    );
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [p]);
  return state;
}

function DocViewer({ path: p, empty }: { path: string | null; empty?: React.ReactNode }) {
  const { url, error } = useDocumentUrl(p);
  if (!p) {
    // The empty state has to say WHY it is empty. "Select a document" reads as
    // "you forgot to click something" when in fact the system found nothing to
    // show — which is itself the answer, and the thing the reviewer must act on.
    return (
      <div className="h-full flex items-center justify-center p-8">
        {empty ?? (
          <span className="text-sm text-muted-foreground">
            Select a document to preview it here
          </span>
        )}
      </div>
    );
  }
  if (error) {
    // The reason, not a blank frame: a reviewer looking at nothing would take
    // the invoice to be empty.
    return (
      <div className="h-full flex items-center justify-center p-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="break-all">{error}</AlertDescription>
        </Alert>
      </div>
    );
  }
  if (!url) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <span className="text-sm text-muted-foreground">Loading the document…</span>
      </div>
    );
  }
  return docType(p) === 'application/pdf' ? (
    // No `sandbox` attribute: an empty sandbox stops Chrome instantiating its
    // PDF viewer, so the document never renders at all. What keeps a hostile
    // PDF inert is the blob type set in fetchDocument (see above), and PDFium
    // rendering it in its own sandboxed process.
    <iframe src={withViewerParams(url, p)} title="Document" className="w-full h-full border-0" />
  ) : (
    // Images cannot execute, so an <img> is both safe and far better than an
    // iframe for a photographed receipt.
    <img src={url} alt="Receipt" className="w-full h-full object-contain bg-muted/20" />
  );
}

// An "Open PDF" tab reads its document from the object URL for as long as it
// stays open (a reload reads it again), so the URL cannot be released earlier —
// and once the tab is closed, nothing else would release it before the admin
// page itself unloads: every PDF opened in a session would stay in memory. The
// watch lives outside React on purpose, so leaving the Invoices page while the
// tab is open still releases the document when the tab closes.
const TAB_CLOSED_POLL_MS = 2000;

function revokeWhenClosed(tab: Window, url: string): void {
  const timer = window.setInterval(() => {
    if (!tab.closed) return;
    window.clearInterval(timer);
    URL.revokeObjectURL(url);
  }, TAB_CLOSED_POLL_MS);
}

/** "Open PDF" in a new tab, through the same authenticated fetch. */
function OpenDocument({ path: p, children }: { path: string; children: React.ReactNode }) {
  const [error, setError] = useState<string | null>(null);
  const open = async () => {
    setError(null);
    // The tab is opened inside the click, before any await: once the handler
    // has awaited, the browser no longer counts it as a user gesture and the
    // popup blocker refuses the tab.
    const tab = window.open('', '_blank');
    if (!tab) {
      setError('The browser blocked the new tab');
      return;
    }
    tab.opener = null;
    let url: string | null = null;
    try {
      url = await fetchDocument(p);
      if (tab.closed) {
        // Closed while the document was still downloading: nothing will show it.
        URL.revokeObjectURL(url);
        return;
      }
      tab.location.href = url;
      revokeWhenClosed(tab, url);
    } catch (e) {
      if (url) URL.revokeObjectURL(url);
      tab.close();
      setError((e as Error).message);
    }
  };
  return (
    <>
      <button type="button" className="text-xs underline" onClick={() => void open()}>
        {children}
      </button>
      {error && <span className="text-xs text-red-600 break-all">{error}</span>}
    </>
  );
}

function BankTab({
  bank,
  view,
  setView,
  onDecided,
}: {
  bank: BankResponse | null;
  view: 'undecided' | 'matched' | 'orphan-invoices';
  setView: (v: 'undecided' | 'matched' | 'orphan-invoices') => void;
  onDecided: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const fileInput = React.useRef<HTMLInputElement>(null);

  const upload = useCallback(
    async (transactionId: string, file: File) => {
      setBusy(true);
      setErr(null);
      try {
        const body = new FormData();
        body.append('transactionId', transactionId);
        body.append('file', file);
        // No Content-Type header: the browser must set the multipart boundary.
        const res = await authFetch(`${API}/bank/upload`, { method: 'POST', body });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.error || `Upload failed: ${res.status}`);
        }
        onDecided();
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [onDecided]
  );

  // Ambiguous first: those have candidates and are the quickest wins. Payments
  // with no invoice follow, largest first — the money that matters most.
  const items: WorkItem[] = useMemo(() => {
    if (!bank) return [];
    const amb = bank.ambiguous.map((a) => ({ tx: a.transaction, candidates: a.candidates, why: a.why }));
    const none = [...bank.unmatchedTransactions]
      .sort((a, b) => b.amount - a.amount)
      .map((t) => ({ tx: t, candidates: [] as HistoryRow[], why: null }));
    const all = [...amb, ...none];
    const q = query.trim().toLowerCase();
    return q ? all.filter((i) => i.tx.merchant.toLowerCase().includes(q) || i.tx.date.includes(q)) : all;
  }, [bank, query]);

  const item = items[index] ?? null;

  // Default the preview to the first thing worth looking at: a candidate
  // invoice if there is one, otherwise the bank's own receipt.
  useEffect(() => {
    if (!item) return setPreview(null);
    setPreview(item.candidates[0]?.pdfPath ?? item.tx.receiptPaths[0] ?? null);
  }, [item?.tx.id]);

  const decide = useCallback<DecideFn>(
    (transactionId, decision, pdfPath) => {
      setBusy(true);
      setErr(null);
      void (async () => {
        try {
          const res = await authFetch(`${API}/bank/decide`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ transactionId, decision, pdfPath }),
          });
          if (!res.ok) {
            const d = await res.json().catch(() => ({}));
            throw new Error(d.error || `Save failed: ${res.status}`);
          }
          // Stay on the same position: the list shrinks under us, so the next
          // item slides into place and the queue can be cleared without ever
          // moving the mouse.
          setIndex((i) => Math.max(0, Math.min(i, items.length - 2)));
          onDecided();
        } catch (e) {
          setErr(e instanceof Error ? e.message : String(e));
        } finally {
          setBusy(false);
        }
      })();
    },
    [items.length, onDecided]
  );

  useEffect(() => {
    if (view !== 'undecided' || !item) return;
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      if (el && ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return;
      if (e.key === 'j') setIndex((i) => Math.min(items.length - 1, i + 1));
      else if (e.key === 'k') setIndex((i) => Math.max(0, i - 1));
      else if (e.key === 'n') {
        decide(item.tx.id, item.tx.receiptPaths.length > 0 ? 'use_receipt' : 'no_invoice');
      }
      else if (e.key === 'i') decide(item.tx.id, 'ignore');
      else if (/^[1-9]$/.test(e.key)) {
        const c = item.candidates[Number(e.key) - 1];
        if (c) decide(item.tx.id, 'link', c.pdfPath);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, item, items.length, decide]);

  if (bank?.notGenerated) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          The invoice agent has not pushed a bank reconciliation yet.
          {bank.hint ? <> {bank.hint}</> : null}
        </AlertDescription>
      </Alert>
    );
  }
  if (!bank) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-3">
      <div className="flex rounded-md border overflow-hidden w-fit">
        {(
          [
            ['undecided', `To decide (${bank.ambiguous.length + bank.unmatchedTransactions.length})`],
            ['matched', `Matched (${bank.matched.length})`],
            ['orphan-invoices', `Unpaid invoices (${bank.unmatchedInvoices.length})`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            className={`px-3 py-1.5 text-sm border-l first:border-l-0 ${view === key ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
            onClick={() => setView(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {err && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{err}</AlertDescription>
        </Alert>
      )}

      {view === 'matched' && (
        <Card>
          <CardContent className="p-0 overflow-x-auto max-h-[70vh]">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 sticky top-0">
                <tr className="text-left">
                  <th className="p-2">Date</th><th className="p-2">Merchant</th>
                  <th className="p-2 text-right">Paid</th><th className="p-2">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {bank.matched.map((m) => (
                  <tr key={m.transaction.id} className="border-b">
                    <td className="p-2 tabular-nums whitespace-nowrap">{m.transaction.date}</td>
                    <td className="p-2">{m.transaction.merchant}</td>
                    <td className="p-2 text-right tabular-nums whitespace-nowrap">
                      {eur(m.transaction.amount, m.transaction.currency)}
                    </td>
                    <td className="p-2 max-w-[340px] truncate" title={m.invoice.pdfPath}>
                      {m.invoice.fileName ?? m.invoice.pdfPath}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {view === 'orphan-invoices' && (
        <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-3">
          <Card>
            <CardContent className="p-2 max-h-[72vh] overflow-y-auto">
              {bank.unmatchedInvoices.map((i) => (
                <button
                  key={i.pdfPath}
                  onClick={() => setPreview(i.pdfPath)}
                  className={`w-full text-left text-sm border-b p-2 hover:bg-muted/40 ${preview === i.pdfPath ? 'bg-muted' : ''}`}
                >
                  <div className="flex justify-between gap-2">
                    <span className="tabular-nums">{i.date}</span>
                    <span className="font-medium">{i.provider}</span>
                    <span className="tabular-nums">
                      {eur((i.amount ?? 0) + (i.taxAmount ?? 0), i.currency ?? 'EUR')}
                    </span>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
          <Card className="overflow-hidden">
            <CardContent className="p-0 h-[72vh]">
              <DocViewer path={preview} />
            </CardContent>
          </Card>
        </div>
      )}

      {view === 'undecided' && (
        items.length === 0 ? (
          <Card>
            <CardContent className="p-10 text-center text-muted-foreground">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-3 text-green-600" />
              Everything is decided.
            </CardContent>
          </Card>
        ) : (
          <div className="flex gap-3 h-[78vh]">
            {/* ── left: the whole queue, always visible ── */}
            <Card className="w-[320px] shrink-0 overflow-hidden">
              <CardContent className="p-0 h-full flex flex-col">
                <div className="p-2 border-b">
                  <Input
                    placeholder="Filter by merchant or date…"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setIndex(0); }}
                    className="h-8"
                  />
                </div>
                <div className="overflow-y-auto flex-1">
                  {items.map((it, n) => (
                    <button
                      key={it.tx.id}
                      onClick={() => setIndex(n)}
                      className={`w-full text-left px-3 py-2 border-b hover:bg-muted/50 ${
                        n === index ? 'bg-muted border-l-2 border-l-primary' : ''
                      }`}
                    >
                      <div className="flex justify-between items-baseline gap-2">
                        <span className="text-sm font-medium truncate">{it.tx.merchant}</span>
                        <span className="text-sm tabular-nums whitespace-nowrap">
                          {eur(it.tx.amount, it.tx.currency)}
                        </span>
                      </div>
                      <div className="flex justify-between items-baseline gap-2">
                        <span className="text-xs text-muted-foreground tabular-nums">{it.tx.date}</span>
                        {/* State the outcome on every row, including the negative
                            one. A blank space where a status belongs reads as
                            "not loaded yet" rather than "we looked and found
                            nothing" — and the difference decides whether the
                            reviewer waits or acts. */}
                        {it.candidates.length > 0 ? (
                          <span className="text-xs text-primary whitespace-nowrap">
                            {it.candidates.length} candidate{it.candidates.length > 1 ? 's' : ''}
                          </span>
                        ) : it.tx.receiptPaths.length > 0 ? (
                          <span className="text-xs text-emerald-600 whitespace-nowrap">
                            receipt available
                          </span>
                        ) : (
                          <span className="text-xs text-amber-600 whitespace-nowrap">
                            no invoice found
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                <div className="p-2 border-t text-xs text-muted-foreground">
                  {index + 1} / {items.length} · <kbd>j</kbd>/<kbd>k</kbd> move · <kbd>1-9</kbd> link ·{' '}
                  <kbd>n</kbd> book · <kbd>i</kbd> ignore
                </div>
              </CardContent>
            </Card>

            {/* ── right: document on top, decision underneath ── */}
            {item && (
              <div className="flex-1 flex flex-col gap-3 min-w-0">
                <Card className="flex-1 overflow-hidden">
                  <CardContent className="p-0 h-full">
                    <DocViewer
                      path={preview}
                      empty={
                        item.candidates.length === 0 && item.tx.receiptPaths.length === 0 ? (
                          <div className="text-center max-w-md space-y-3">
                            <FileX className="h-10 w-10 mx-auto text-muted-foreground" />
                            <div className="text-lg font-medium">No matching invoice found</div>
                            <p className="text-sm text-muted-foreground">
                              Nothing in the mailbox matches{' '}
                              <span className="font-medium text-foreground">
                                {eur(item.tx.amount, item.tx.currency)}
                              </span>{' '}
                              from <span className="font-medium text-foreground">{item.tx.merchant}</span>{' '}
                              on {item.tx.date}, and Revolut has no receipt for it either.
                            </p>
                            <p className="text-sm text-muted-foreground">
                              If the invoice exists on the vendor's own dashboard, use{' '}
                              <span className="font-medium text-foreground">Upload invoice</span> below.
                              Otherwise{' '}
                              <span className="font-medium text-foreground">Book without document</span>{' '}
                              — the expense will be backed by the bank line alone — or{' '}
                              <span className="font-medium text-foreground">Not an expense</span> if it
                              does not belong in the accounts.
                            </p>
                          </div>
                        ) : undefined
                      }
                    />
                  </CardContent>
                </Card>

                <Card className="shrink-0">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="min-w-[200px]">
                        <div className="text-lg font-semibold tabular-nums leading-tight">
                          {eur(item.tx.amount, item.tx.currency)}
                          {item.tx.origCurrency && item.tx.origCurrency !== item.tx.currency && (
                            <span className="text-sm font-normal text-muted-foreground">
                              {' '}({eur(item.tx.origAmount ?? 0, item.tx.origCurrency)})
                            </span>
                          )}
                        </div>
                        <div className="text-sm">
                          {item.tx.merchant}
                          <span className="text-muted-foreground tabular-nums"> · {item.tx.date}</span>
                        </div>
                      </div>

                      <div className="flex gap-2 flex-wrap flex-1">
                        {item.candidates.map((c, n) => (
                          <div key={c.pdfPath} className="flex rounded-md border overflow-hidden">
                            {/* Split control: the left half previews, the right half
                                commits. Separating them means a glance at a candidate
                                can never be mistaken for choosing it. */}
                            <button
                              className={`px-2 py-1.5 text-xs hover:bg-muted ${preview === c.pdfPath ? 'bg-muted font-medium' : ''}`}
                              onClick={() => setPreview(c.pdfPath)}
                              title={c.fileName}
                            >
                              <kbd className="border rounded px-1 mr-1">{n + 1}</kbd>
                              {c.date}
                            </button>
                            <Button
                              size="sm"
                              className="rounded-none h-auto"
                              disabled={busy}
                              onClick={() => decide(item.tx.id, 'link', c.pdfPath)}
                            >
                              Link
                            </Button>
                          </div>
                        ))}
                        {item.tx.receiptPaths.length > 0 && (
                          <button
                            className={`px-2 py-1.5 text-xs border rounded-md hover:bg-muted ${preview === item.tx.receiptPaths[0] ? 'bg-muted font-medium' : ''}`}
                            onClick={() => setPreview(item.tx.receiptPaths[0])}
                          >
                            Bank receipt
                          </button>
                        )}
                        {item.candidates.length === 0 && item.tx.receiptPaths.length === 0 && (
                          <span className="text-sm text-muted-foreground self-center">
                            No invoice in the mailbox matches this payment.
                          </span>
                        )}
                      </div>

                      <div className="flex gap-2 shrink-0">
                        {/* Uploading IS the answer for a payment whose invoice
                            only exists on the vendor's dashboard — far more
                            common than a missing invoice, so it sits beside the
                            other decisions rather than hidden behind a menu. */}
                        <input
                          ref={fileInput}
                          type="file"
                          accept="application/pdf,image/jpeg,image/png"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) void upload(item.tx.id, f);
                            e.target.value = '';
                          }}
                        />
                        <Button variant="secondary" size="sm" disabled={busy}
                          onClick={() => fileInput.current?.click()}>
                          <Upload className="h-4 w-4 mr-1" />Upload invoice
                        </Button>
                        {item.tx.receiptPaths.length > 0 ? (
                          // The bank receipt IS the supporting document — a
                          // restaurant ticket or taxi slip carries the merchant,
                          // date, total and VAT, and no emailed invoice will ever
                          // exist for it. Booking it is the normal outcome here,
                          // so it is the primary action, not a fallback.
                          <Button size="sm" disabled={busy}
                            onClick={() => decide(item.tx.id, 'use_receipt')}>
                            <CheckCircle2 className="h-4 w-4 mr-1" />Book with this receipt
                          </Button>
                        ) : (
                          // Nothing at all: the expense goes in backed only by a
                          // bank line. Named for what it is, because that is a
                          // real tax exposure an accountant will want to chase.
                          <Button variant="outline" size="sm" disabled={busy}
                            onClick={() => decide(item.tx.id, 'no_invoice')}>
                            <FileX className="h-4 w-4 mr-1" />Book without document
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" disabled={busy}
                          onClick={() => decide(item.tx.id, 'ignore')}>
                          Not an expense
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// CFO figures.
//
// Deliberately almost no charts: every question here ("how much is unsettled?",
// "which invoice is unpaid?") is answered by an exact number, and a bar chart of
// eight vendors would replace precision with decoration. Stat tiles and tables,
// with an inline magnitude bar only where scanning a ranking helps.
//
// Colour: status hues are the reserved four and ALWAYS ship with an icon and a
// word — two of them sit below 3:1 on a light surface by design, so hue never
// carries the meaning alone. Bars use one blue; their LENGTH encodes magnitude,
// so colour is identity, not scale.
// ---------------------------------------------------------------------------

const STATUS = {
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
} as const;

const BAR = '#2a78d6';

interface Analytics {
  generatedAt: string | null;
  bankGeneratedAt: string | null;
  documents: {
    total: number; booked: number; notInvoices: number; unresolved: number;
    pricedByHuman: number; withSourceEmail: number;
  };
  spendByCurrency: Record<string, number>;
  byVendor: Array<{ vendor: string; count: number; byCurrency: Record<string, number> }>;
  byMonth: Array<{ month: string; count: number; byCurrency: Record<string, number> }>;
  reconciliation: {
    transactions: number; skipped: number;
    matched: number; matchedAmount: Record<string, number>;
    ambiguous: number; ambiguousAmount: Record<string, number>;
    undecided: number; undecidedAmount: Record<string, number>;
    decided: number; undocumented: number; undocumentedAmount: Record<string, number>;
    // Decided and evidenced (uploaded invoice / accepted bank receipt), and
    // everything out of the accounts. Both were computed nowhere before, so
    // deciding a payment used to remove its amount from every figure here.
    documented: number; documentedAmount: Record<string, number>;
    excluded: number; excludedAmount: Record<string, number>;
    withReceipt: number;
  } | null;
  unpaidInvoices: Array<{ provider: string; date: string | null; currency: string | null; gross: number; pdfPath: string }>;
}

function amount(n: number, cur = 'EUR'): string {
  return `${cur} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * One figure per currency, never a sum across them: "EUR 1,200.00 · USD 340.00".
 * The backend keys every reconciliation total by currency for exactly that
 * reason — a USD payment added to euros and labelled EUR was a number nobody
 * could act on. EUR first, then alphabetical; nothing at all reads as zero.
 */
function amounts(by: Record<string, number>): string {
  const keys = Object.keys(by).sort((a, b) => (a === 'EUR' ? -1 : b === 'EUR' ? 1 : a.localeCompare(b)));
  return keys.length ? keys.map((c) => amount(by[c], c)).join(' · ') : amount(0);
}

/** Add per-currency totals, currency by currency. */
function addAmounts(...parts: Record<string, number>[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const p of parts) for (const [c, v] of Object.entries(p)) out[c] = (out[c] ?? 0) + v;
  return out;
}

/** A single figure. No plot, so no hover layer — the number is the whole story. */
function Tile({
  label, value, sub, tone, icon: Icon,
}: {
  label: string; value: string; sub?: string;
  tone?: keyof typeof STATUS; icon?: React.ElementType;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold tabular-nums mt-1 flex items-center gap-2">
          {/* Icon + colour together: the icon is what makes the state readable
              without relying on hue, which two of the four statuses cannot carry
              on a light surface. */}
          {Icon && tone && <Icon className="h-5 w-5 shrink-0" style={{ color: STATUS[tone] }} />}
          <span>{value}</span>
        </div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

/** Ranking row: the bar is a scanning aid, the number stays exact beside it. */
function BarRow({ label, value, max, count, currency }: {
  label: string; value: number; max: number; count: number; currency: string;
}) {
  const pct = max > 0 ? Math.max(1.5, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1.5 border-b last:border-0">
      <span className="w-36 shrink-0 text-sm truncate" title={label}>{label}</span>
      <div className="flex-1 h-2.5 bg-muted rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: BAR }} />
      </div>
      <span className="w-16 shrink-0 text-xs text-muted-foreground text-right tabular-nums">{count}×</span>
      <span className="w-32 shrink-0 text-sm text-right tabular-nums">{amount(value, currency)}</span>
    </div>
  );
}

// ── Chart primitives ───────────────────────────────────────────────
//
// Palette validated with the dataviz validator in BOTH modes (adjacent-pair
// mode, which is what stacks and bars use): all checks pass, worst adjacent CVD
// ΔE 9.1 light / 8.4 dark. Two light-mode slots sit below 3:1 on the surface, so
// the relief rule applies — every segment carries a visible label and the same
// figures exist as a table below.
//
// Dark steps are the palette's own dark column, not a flip of the light values.

const SERIES = {
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'],
  dark: ['#3987e5', '#d95926', '#199e70', '#c98500'],
} as const;

function useSeries(): readonly string[] {
  // Follows the OS setting; the app's own toggle stamps data-theme, which wins.
  const [dark, setDark] = React.useState(false);
  React.useEffect(() => {
    const read = () => {
      const stamped = document.documentElement.getAttribute('data-theme');
      setDark(stamped ? stamped === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches);
    };
    read();
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', read);
    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => { mq.removeEventListener('change', read); obs.disconnect(); };
  }, []);
  return dark ? SERIES.dark : SERIES.light;
}

/**
 * Spend over time. Bars, not a line: the months are discrete billing periods,
 * and a line between them would imply a continuum that does not exist.
 */
function MonthlyBars({ months }: { months: Array<{ month: string; count: number; byCurrency: Record<string, number> }> }) {
  const series = useSeries();
  const [hover, setHover] = React.useState<number | null>(null);
  const data = months.map((m) => ({ ...m, eur: m.byCurrency.EUR ?? 0 })).filter((m) => m.eur > 0);
  const max = Math.max(...data.map((m) => m.eur), 0);
  if (!data.length) return <p className="text-sm text-muted-foreground p-3">No monthly data.</p>;

  return (
    <div className="p-3">
      <div className="flex items-end gap-2 h-48" role="img"
        aria-label={`Spend by month: ${data.map((m) => `${m.month} ${m.eur.toFixed(0)} EUR`).join(', ')}`}>
        {data.map((m, i) => (
          <div key={m.month} className="flex-1 flex flex-col items-center gap-1 h-full justify-end relative"
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            {hover === i && (
              <div className="absolute bottom-full mb-1 z-10 rounded border bg-popover px-2 py-1 text-xs whitespace-nowrap shadow">
                <div className="font-medium">{m.month}</div>
                <div className="tabular-nums">{amount(m.eur)}</div>
                <div className="text-muted-foreground">{m.count} invoices</div>
              </div>
            )}
            {/* Value on every bar: only four of them, and the relief rule
                requires visible labels for the sub-3:1 light steps anyway. */}
            <span className="text-xs tabular-nums text-muted-foreground">
              {Math.round(m.eur).toLocaleString('en-US')}
            </span>
            <div
              className="w-full rounded-t"
              style={{
                height: `${max > 0 ? Math.max(2, (m.eur / max) * 100) : 0}%`,
                background: series[0],
                opacity: hover === null || hover === i ? 1 : 0.55,
              }}
            />
            <span className="text-xs text-muted-foreground">{m.month.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Where the bank payments stand, as one stacked bar.
 *
 * A single 100% bar because the question is "what share is still open?", which
 * is a part-of-whole. Segments carry a 2px surface gap so adjacent fills never
 * touch, and each is direct-labelled — colour alone never names a state.
 */
function ReconciliationBar({ r }: { r: NonNullable<Analytics['reconciliation']> }) {
  const series = useSeries();
  const [hover, setHover] = React.useState<number | null>(null);

  // Every payment the reconciliation knows about lands in exactly one segment:
  // the backend classifies each one once and these are slices of that
  // partition, so the bar always represents the whole. Previously a decided
  // payment left `ambiguous`/`undecided` and entered nothing, so the bar
  // silently SHRANK as the queue was worked through.
  //
  // Four segments, not six: "will be booked" merges the evidenced and the
  // unevidenced because the question this bar answers is what share is still
  // OPEN. Splitting them would need two more categorical hues, and the palette
  // here is a validated four-step — an unvalidated fifth and sixth hue is
  // exactly the colourblind-safety guess that is not worth making for detail
  // the tile above and the table below already give in full, with amounts.
  const segments = [
    { label: 'Will be booked', n: r.matched + r.documented + r.undocumented,
      amt: addAmounts(r.matchedAmount, r.documentedAmount, r.undocumentedAmount), color: series[0] },
    { label: 'Ambiguous', n: r.ambiguous, amt: r.ambiguousAmount, color: series[1] },
    { label: 'No invoice', n: r.undecided, amt: r.undecidedAmount, color: series[2] },
    { label: 'Excluded', n: r.excluded, amt: r.excludedAmount, color: series[3] },
  ].filter((s) => s.n > 0);
  const total = segments.reduce((s, x) => s + x.n, 0);
  if (!total) return null;

  return (
    <div className="p-3 space-y-2">
      <div className="flex h-10 w-full gap-[2px]" role="img"
        aria-label={segments.map((s) => `${s.label} ${s.n}`).join(', ')}>
        {segments.map((s, i) => (
          <div key={s.label} className="relative h-full first:rounded-l last:rounded-r overflow-hidden"
            style={{
              width: `${(s.n / total) * 100}%`,
              background: s.color,
              opacity: hover === null || hover === i ? 1 : 0.55,
            }}
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            {(s.n / total) > 0.07 && (
              <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
                {s.n}
              </span>
            )}
            {hover === i && (
              <div className="absolute bottom-full mb-1 left-0 z-10 rounded border bg-popover px-2 py-1 text-xs whitespace-nowrap shadow text-foreground">
                <div className="font-medium">{s.label}</div>
                <div className="tabular-nums">{s.n} payments{Object.keys(s.amt).length ? ` · ${amounts(s.amt)}` : ''}</div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Legend always present for ≥2 series, so identity never rests on hue. */}
      <div className="flex gap-4 flex-wrap text-xs">
        {segments.map((s) => (
          <span key={s.label} className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
            <span className="text-muted-foreground">
              {s.label} — {Math.round((s.n / total) * 100)}%
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

function AnalyticsTab({ data }: { data: Analytics | null }) {
  if (!data) return <Skeleton className="h-64 w-full" />;
  const r = data.reconciliation;
  const d = data.documents;

  const eurVendors = data.byVendor
    .map((v) => ({ ...v, eur: v.byCurrency.EUR ?? 0 }))
    .filter((v) => v.eur > 0)
    .sort((a, b) => b.eur - a.eur);
  const maxVendor = Math.max(...eurVendors.map((v) => v.eur), 0);

  const months = data.byMonth.filter((m) => (m.byCurrency.EUR ?? 0) > 0);
  const maxMonth = Math.max(...months.map((m) => m.byCurrency.EUR ?? 0), 0);

  // Settled means "the booking run knows what to do with it" — an automatic
  // match, a human link, an uploaded invoice, an accepted receipt, or an
  // explicit book-without-document. Counting only `matched` made the figure
  // FALL every time a reviewer answered one of the other questions, which is
  // the opposite of the progress it claims to show. Excluded lines are in
  // neither term: they never enter the accounts, so they are not work.
  const settled = r ? r.matched + r.documented + r.undocumented : 0;
  const open = r ? r.ambiguous + r.undecided : 0;
  const settledPct = settled + open > 0 ? Math.round((settled / (settled + open)) * 100) : 0;

  return (
    <div className="space-y-5">
      {/* ── What is booked ── */}
      <div>
        <h3 className="text-sm font-medium mb-2">Booked in Invoice Ninja</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(data.spendByCurrency).map(([cur, total]) => (
            <Tile key={cur} label={`Total spend (${cur})`} value={amount(total, cur)}
              sub={`${d.booked} expenses, VAT included`} />
          ))}
          <Tile label="Documents processed" value={String(d.total)}
            sub={`${d.booked} booked · ${d.notInvoices} rejected as not a bill`} />
          <Tile label="With a source email" value={`${d.withSourceEmail}%`}
            tone={d.withSourceEmail >= 95 ? 'good' : 'warning'}
            icon={d.withSourceEmail >= 95 ? CheckCircle2 : AlertCircle}
            sub="traceable back to the message it came from" />
        </div>
      </div>

      {/* ── What is NOT finished — the reason this tab exists ── */}
      {r && (
        <div>
          <h3 className="text-sm font-medium mb-2">Not yet settled</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Payments settled" value={`${settledPct}%`}
              tone={settledPct >= 80 ? 'good' : settledPct >= 40 ? 'warning' : 'serious'}
              icon={settledPct >= 80 ? CheckCircle2 : AlertCircle}
              sub={`${settled} of ${settled + open} payments decided`} />
            <Tile label="Awaiting a decision" value={String(r.ambiguous + r.undecided)}
              tone={r.ambiguous + r.undecided > 0 ? 'warning' : 'good'}
              icon={r.ambiguous + r.undecided > 0 ? Clock : CheckCircle2}
              sub={amounts(addAmounts(r.ambiguousAmount, r.undecidedAmount)) + ' unreviewed'} />
            <Tile label="Invoices with no payment" value={String(data.unpaidInvoices.length)}
              tone={data.unpaidInvoices.length > 0 ? 'serious' : 'good'}
              icon={data.unpaidInvoices.length > 0 ? AlertCircle : CheckCircle2}
              sub="paid another way, or genuinely unpaid" />
            <Tile label="Expenses with no document" value={String(r.undocumented)}
              tone={r.undocumented > 0 ? 'critical' : 'good'}
              icon={r.undocumented > 0 ? FileX : CheckCircle2}
              sub={r.undocumented > 0 ? amounts(r.undocumentedAmount) + ' — tax exposure' : 'every expense is evidenced'} />
          </div>

          <Card className="mt-3">
            <CardContent className="p-0">
              <ReconciliationBar r={r} />
            </CardContent>
          </Card>

          <Card className="mt-3">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40">
                  <tr className="text-left">
                    <th className="p-2">Bank payments ({r.transactions})</th>
                    <th className="p-2 text-right">Count</th>
                    <th className="p-2 text-right">Amount</th>
                  </tr>
                </thead>
                {/* These six rows are a PARTITION: the backend puts every
                    payment in exactly one of them, so they add up to the
                    header count. The total row is not decoration — it is the
                    invariant made visible, and it is why a decided payment can
                    no longer quietly leave the page. */}
                <tbody>
                  <tr className="border-b">
                    <td className="p-2">✅ Matched to an invoice</td>
                    <td className="p-2 text-right tabular-nums">{r.matched}</td>
                    <td className="p-2 text-right tabular-nums">{amounts(r.matchedAmount)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-2">📎 Booked with an uploaded invoice or a bank receipt</td>
                    <td className="p-2 text-right tabular-nums">{r.documented}</td>
                    <td className="p-2 text-right tabular-nums">{amounts(r.documentedAmount)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-2">
                      🚫 Booked with no document
                      <span className="text-xs text-muted-foreground"> — backed by the bank line alone</span>
                    </td>
                    <td className="p-2 text-right tabular-nums">{r.undocumented}</td>
                    <td className="p-2 text-right tabular-nums">{amounts(r.undocumentedAmount)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-2">❓ Ambiguous — needs a choice</td>
                    <td className="p-2 text-right tabular-nums">{r.ambiguous}</td>
                    <td className="p-2 text-right tabular-nums">{amounts(r.ambiguousAmount)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-2">
                      💳 No invoice found
                      <span className="text-xs text-muted-foreground"> — {r.withReceipt} have a bank receipt</span>
                    </td>
                    <td className="p-2 text-right tabular-nums">{r.undecided}</td>
                    <td className="p-2 text-right tabular-nums">{amounts(r.undecidedAmount)}</td>
                  </tr>
                  <tr className="border-b text-muted-foreground">
                    <td className="p-2">⏭ Excluded (reversals, fees, pending, not an expense)</td>
                    <td className="p-2 text-right tabular-nums">{r.excluded}</td>
                    <td className="p-2 text-right tabular-nums">{amounts(r.excludedAmount)}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="border-t font-medium">
                    <td className="p-2">All payments</td>
                    <td className="p-2 text-right tabular-nums">
                      {r.matched + r.documented + r.undocumented + r.ambiguous + r.undecided + r.excluded}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {amounts(addAmounts(r.matchedAmount, r.documentedAmount, r.undocumentedAmount,
                        r.ambiguousAmount, r.undecidedAmount, r.excludedAmount))}
                    </td>
                  </tr>
                  <tr className="text-muted-foreground">
                    {/* A memo line, deliberately outside the partition above:
                        a human decision REPLACES a payment's bucket rather
                        than adding one, so adding this in would double-count. */}
                    <td className="p-2">🧑 of which decided by a human</td>
                    <td className="p-2 text-right tabular-nums">{r.decided}</td>
                    <td className="p-2 text-right">—</td>
                  </tr>
                </tfoot>
              </table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Chase list ── */}
      {data.unpaidInvoices.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-2">
            Invoices with no matching payment — largest first
          </h3>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40">
                  <tr className="text-left">
                    <th className="p-2">Date</th><th className="p-2">Vendor</th>
                    <th className="p-2 text-right">Amount</th><th className="p-2" />
                  </tr>
                </thead>
                <tbody>
                  {data.unpaidInvoices.map((i) => (
                    <tr key={i.pdfPath} className="border-b">
                      <td className="p-2 tabular-nums whitespace-nowrap">{i.date}</td>
                      <td className="p-2">{i.provider}</td>
                      <td className="p-2 text-right tabular-nums">{amount(i.gross, i.currency ?? 'EUR')}</td>
                      <td className="p-2 text-right">
                        <OpenDocument path={i.pdfPath}>PDF</OpenDocument>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Rankings ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-medium mb-2">Spend by vendor (EUR, VAT incl.)</h3>
          <Card><CardContent className="p-3">
            {eurVendors.map((v) => (
              <BarRow key={v.vendor} label={v.vendor} value={v.eur} max={maxVendor}
                count={v.count} currency="EUR" />
            ))}
          </CardContent></Card>
        </div>
        <div>
          <h3 className="text-sm font-medium mb-2">Spend by month (EUR, VAT incl.)</h3>
          <Card><CardContent className="p-0">
            <MonthlyBars months={data.byMonth} />
          </CardContent></Card>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Invoices generated {data.generatedAt ? new Date(data.generatedAt).toLocaleString() : '—'}
        {data.bankGeneratedAt && ` · bank reconciliation ${new Date(data.bankGeneratedAt).toLocaleString()}`}
      </p>
    </div>
  );
}

function Field({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground min-w-[90px]">{k}</span>
      <span className="break-all">{v}</span>
    </div>
  );
}

export default InvoiceReview;
